import React, { useState, useEffect, useMemo } from "react";
import { db, storage } from "../../firebase";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../../AuthContext";
import { extractEventsFromCalendarImage } from "../../utils/firebaseFunctions";

const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const CATEGORIES = [
  { value: "food", label: "Daily Food" },
  { value: "drink", label: "Daily Drink" },
  { value: "happyhour", label: "Happy Hour" },
  { value: "latenight", label: "Late Night" },
  { value: "birthday", label: "Birthday" },
];

// Maps the AI's special-category wording back to this form's short values.
const SPECIAL_CATEGORY_TO_VALUE = {
  "Daily Food": "food",
  "Daily Drink": "drink",
  "Happy Hour": "happyhour",
  "Late Night": "latenight",
  "Birthday": "birthday",
};

function formatTime(time24) {
  if (!time24) return "";
  const [h, m] = time24.split(":");
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${m} ${period}`;
}

// Turns "6:00pm" / "6pm" / "6:00 PM" into "18:00" for time inputs — same
// helper used by the calendar-scan feature.
function parseTimeTo24h(rawTime) {
  if (!rawTime) return "";
  const match = String(rawTime).trim().match(/^(\d{1,2}):?(\d{2})?\s*([AaPp][Mm])?$/);
  if (!match) return "";
  let hour = parseInt(match[1], 10);
  const minute = match[2] || "00";
  const period = match[3] ? match[3].toLowerCase() : null;
  if (period === "pm" && hour !== 12) hour += 12;
  if (period === "am" && hour === 12) hour = 0;
  return `${String(hour).padStart(2, "0")}:${minute}`;
}

// Resizes the photo down before sending it for reading, same as the
// calendar-scan feature — keeps uploads small and fast.
function compressImageToBase64(file, maxWidth = 1200, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve({ base64: dataUrl.split(",")[1], mediaType: "image/jpeg" });
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Specials() {
  const { user, userBars } = useAuth();

  const myBars = useMemo(
    () => userBars.filter((bar) => bar.ownerId === user?.uid),
    [userBars, user]
  );

  const [selectedBarId, setSelectedBarId] = useState("");
  const [category, setCategory] = useState("food");
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [recurrence, setRecurrence] = useState("daily");
  const [days, setDays] = useState([]);
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [scanNote, setScanNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const selectedBar = myBars.find((b) => b.id === selectedBarId);
  const existingSpecials = selectedBar?.specials || [];
  const existingBirthdayDeals = selectedBar?.birthdayDeals || [];

  useEffect(() => {
    setCategory("food");
    setTitle("");
    setDetails("");
    setRecurrence("daily");
    setDays([]);
    setDate("");
    setStartTime("");
    setEndTime("");
    setPhotoUrl("");
    setScanNote("");
    setEditingId(null);
  }, [selectedBarId]);

  const toggleDay = (day) => {
    setDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  // Uploads the photo AND reads it with AI at the same time — the photo
  // gets attached either way, and whatever the AI can confidently read
  // (title, category, schedule) gets filled in for you to review and edit
  // before saving. Nothing saves automatically from this.
  const uploadPhoto = async (file) => {
    if (!selectedBarId || !file) return;
    setUploadingPhoto(true);
    setScanNote("");

    try {
      const [uploadResult, extractResult] = await Promise.allSettled([
        (async () => {
          const fileRef = ref(storage, `special-photos/${selectedBarId}/${Date.now()}-${file.name}`);
          await uploadBytes(fileRef, file);
          return getDownloadURL(fileRef);
        })(),
        (async () => {
          const { base64, mediaType } = await compressImageToBase64(file);
          return extractEventsFromCalendarImage({ imageBase64: base64, mediaType, barId: selectedBarId });
        })(),
      ]);

      if (uploadResult.status === "fulfilled") {
        setPhotoUrl(uploadResult.value);
      } else {
        console.error("Photo upload failed:", uploadResult.reason);
      }

      if (extractResult.status === "fulfilled") {
        const items = extractResult.value.draftEvents || [];
        applyExtractedItem(items);
      } else {
        console.error("Photo reading failed:", extractResult.reason);
        setScanNote("Photo attached, but couldn't automatically read the details — fill them in below.");
      }
    } finally {
      setUploadingPhoto(false);
    }
  };

  const applyExtractedItem = (items) => {
    if (items.length === 0) {
      setScanNote("Photo attached, but nothing readable was found on it — fill in the details below.");
      return;
    }

    const specialItems = items.filter((i) => i.itemType === "special");
    const chosen = specialItems[0] || items[0];
    const isSpecialShaped = chosen.itemType === "special";

    if (chosen.title) setTitle(chosen.title);
    if (chosen.details) setDetails(chosen.details);

    if (isSpecialShaped) {
      const mappedCategory = SPECIAL_CATEGORY_TO_VALUE[chosen.specialCategory];
      if (mappedCategory) setCategory(mappedCategory);
      if (chosen.recurrence) setRecurrence(chosen.recurrence);
      if (chosen.days) setDays(chosen.days);
      if (chosen.startTime) setStartTime(parseTimeTo24h(chosen.startTime));
      if (chosen.endTime) setEndTime(parseTimeTo24h(chosen.endTime));
    }

    const notes = [];
    if (!isSpecialShaped) {
      notes.push("This read more like an event than a special — double-check the schedule fields below.");
    }
    if (items.length > 1) {
      notes.push(`Found ${items.length} items on this photo — only the first was filled in. Upload again or add the rest by hand if needed.`);
    }
    if (chosen.needsReview) {
      notes.push(chosen.reviewNote || "Double-check the details below — something was unclear on the photo.");
    }
    setScanNote(notes.join(" "));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBarId || !title) return;
    setSaving(true);

    const itemData = {
      id: editingId || Date.now() + Math.random(),
      category,
      title,
      details,
      recurrence: category === "birthday" ? "standing" : recurrence,
      days: recurrence === "days" ? days : [],
      date: recurrence === "one-time" ? date : "",
      startTime,
      endTime,
      photoUrl,
    };

    const barRef = doc(db, "bars", selectedBarId);

    if (category === "birthday") {
      const updated = editingId
        ? existingBirthdayDeals.map((s) => (s.id === editingId ? itemData : s))
        : [...existingBirthdayDeals, itemData];
      await updateDoc(barRef, { birthdayDeals: updated });
    } else {
      const updated = editingId
        ? existingSpecials.map((s) => (s.id === editingId ? itemData : s))
        : [...existingSpecials, itemData];
      await updateDoc(barRef, { specials: updated });
    }

    setTitle("");
    setDetails("");
    setDays([]);
    setDate("");
    setStartTime("");
    setEndTime("");
    setPhotoUrl("");
    setScanNote("");
    setEditingId(null);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setCategory(item.category);
    setTitle(item.title);
    setDetails(item.details || "");
    setRecurrence(item.recurrence === "standing" ? "daily" : item.recurrence);
    setDays(item.days || []);
    setDate(item.date || "");
    setStartTime(item.startTime || "");
    setEndTime(item.endTime || "");
    setPhotoUrl(item.photoUrl || "");
    setScanNote("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setTitle("");
    setDetails("");
    setDays([]);
    setDate("");
    setStartTime("");
    setEndTime("");
    setPhotoUrl("");
    setScanNote("");
  };

  const removeSpecial = async (item) => {
    if (!window.confirm("Remove this special? This can't be undone.")) return;
    const barRef = doc(db, "bars", selectedBarId);
    if (item.category === "birthday") {
      await updateDoc(barRef, {
        birthdayDeals: existingBirthdayDeals.filter((s) => s.id !== item.id),
      });
    } else {
      await updateDoc(barRef, {
        specials: existingSpecials.filter((s) => s.id !== item.id),
      });
    }
  };

  const describeSchedule = (item) => {
    if (item.recurrence === "standing") return "Standing offer";
    if (item.recurrence === "daily") {
      return item.startTime && item.endTime
        ? `Daily, ${formatTime(item.startTime)} to ${formatTime(item.endTime)}`
        : "Daily";
    }
    if (item.recurrence === "days") {
      const dayList = item.days?.join(", ") || "";
      return item.startTime && item.endTime
        ? `${dayList}, ${formatTime(item.startTime)} to ${formatTime(item.endTime)}`
        : dayList;
    }
    if (item.recurrence === "one-time") {
      return item.startTime && item.endTime
        ? `${item.date}, ${formatTime(item.startTime)} to ${formatTime(item.endTime)}`
        : item.date;
    }
    return "";
  };

  const inputStyle = {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "none",
    fontSize: "15px",
    boxSizing: "border-box",
  };
  const labelStyle = { display: "block", marginBottom: "8px", fontWeight: 600 };

  const renderItemRow = (item) => (
    <div
      key={item.id}
      style={{
        background: "#0D0B1A",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "10px",
        padding: "15px",
        marginBottom: "10px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "12px",
      }}
    >
      <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", minWidth: 0 }}>
        {item.photoUrl && (
          <img
            src={item.photoUrl}
            alt=""
            style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "8px", flexShrink: 0 }}
          />
        )}
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 600 }}>{item.title}</p>
          {item.category !== "birthday" && (
            <p style={{ margin: "4px 0 0", color: "#B8B0C8", fontSize: "16px" }}>
              {describeSchedule(item)}
            </p>
          )}
          {item.details && (
            <p style={{ margin: "4px 0 0", color: "#B8B0C8", fontSize: "16px" }}>{item.details}</p>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
        <button
          onClick={() => startEdit(item)}
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "8px",
            color: "#fff",
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          Edit
        </button>
        <button
          onClick={() => removeSpecial(item)}
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "8px",
            color: "#F472B6",
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          Remove
        </button>
      </div>
    </div>
  );

  if (myBars.length === 0) {
    return (
      <p style={{ color: "#fff" }}>
        You need to claim a bar first before you can add specials.
      </p>
    );
  }

  return (
    <div style={{ color: "#fff" }}>
      <h2 style={{ marginBottom: "10px" }}>Daily Specials &amp; Happy Hours</h2>
      <p style={{ marginBottom: "20px", color: "#B8B0C8" }}>
        Food, drink, happy hour, late night, and birthday deals — recurring or one-time.
      </p>

      <form
        onSubmit={handleSubmit}
        style={{
          background: "#0D0B1A",
          padding: "24px",
          borderRadius: "12px",
          maxWidth: "520px",
          marginBottom: "30px",
        }}
      >
        <label style={labelStyle}>Venue</label>
        <select
          value={selectedBarId}
          onChange={(e) => setSelectedBarId(e.target.value)}
          required
          style={{ ...inputStyle, marginBottom: "18px" }}
        >
          <option value="">Select a bar</option>
          {myBars.map((bar) => (
            <option key={bar.id} value={bar.id}>{bar.name || "Unnamed Bar"}</option>
          ))}
        </select>

        <label style={labelStyle}>Photo, optional</label>
        <p style={{ fontSize: "16px", color: "#B8B0C8", margin: "0 0 10px" }}>
          Got a flyer or handwritten sign for this? Upload it and we'll try to read the title, category,
          and schedule for you — just double-check everything below before saving.
        </p>
        {photoUrl ? (
          <div style={{ position: "relative", marginBottom: "10px", maxWidth: "200px" }}>
            <img
              src={photoUrl}
              alt="Special preview"
              style={{ width: "100%", borderRadius: "8px", display: "block" }}
            />
            <button
              type="button"
              onClick={() => setPhotoUrl("")}
              style={{
                position: "absolute",
                top: "6px",
                right: "6px",
                background: "rgba(0,0,0,0.6)",
                border: "none",
                borderRadius: "6px",
                color: "#fff",
                width: "26px",
                height: "26px",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              ×
            </button>
          </div>
        ) : (
          <label
            style={{
              display: "block",
              border: "1px dashed rgba(255,255,255,0.3)",
              borderRadius: "8px",
              padding: "16px",
              textAlign: "center",
              cursor: "pointer",
              marginBottom: "10px",
              color: "#B8B0C8",
            }}
          >
            {uploadingPhoto ? "Reading photo…" : "+ Add a photo"}
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              disabled={uploadingPhoto || !selectedBarId}
              onChange={(e) => uploadPhoto(e.target.files[0])}
            />
          </label>
        )}
        {scanNote && (
          <p style={{ fontSize: "16px", color: "#F59E0B", margin: "0 0 18px" }}>{scanNote}</p>
        )}

        <label style={labelStyle}>Type</label>
        <div style={{ display: "flex", gap: "8px", marginBottom: "18px", flexWrap: "wrap" }}>
          {CATEGORIES.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => setCategory(c.value)}
              style={{
                flex: "1 1 auto",
                padding: "10px",
                borderRadius: "8px",
                border: category === c.value ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                background: category === c.value ? "rgba(168,85,247,0.15)" : "transparent",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              {c.label}
            </button>
          ))}
        </div>

        <label style={labelStyle}>Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={category === "birthday" ? "Free birthday shot with ID" : "$2 tap beer"}
          required
          style={{ ...inputStyle, marginBottom: "18px" }}
        />

        <label style={labelStyle}>Details, optional</label>
        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder="Any extra details customers should see"
          style={{ ...inputStyle, marginBottom: "18px", height: "70px" }}
        />

        {category !== "birthday" && (
          <>
            <label style={labelStyle}>When does this run?</label>
            <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
              <button
                type="button"
                onClick={() => setRecurrence("daily")}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "8px",
                  border: recurrence === "daily" ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                  background: recurrence === "daily" ? "rgba(168,85,247,0.15)" : "transparent",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Every day
              </button>
              <button
                type="button"
                onClick={() => setRecurrence("days")}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "8px",
                  border: recurrence === "days" ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                  background: recurrence === "days" ? "rgba(168,85,247,0.15)" : "transparent",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Specific days
              </button>
              <button
                type="button"
                onClick={() => setRecurrence("one-time")}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "8px",
                  border: recurrence === "one-time" ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                  background: recurrence === "one-time" ? "rgba(168,85,247,0.15)" : "transparent",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                One-time
              </button>
            </div>

            {recurrence === "days" && (
              <div style={{ display: "flex", gap: "6px", marginBottom: "18px", flexWrap: "wrap" }}>
                {DAYS_SHORT.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: "6px",
                      border: days.includes(day) ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                      background: days.includes(day) ? "rgba(168,85,247,0.2)" : "transparent",
                      color: "#fff",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  >
                    {day}
                  </button>
                ))}
              </div>
            )}

            {recurrence === "one-time" && (
              <div style={{ marginBottom: "18px" }}>
                <label style={labelStyle}>Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>
            )}

            <label style={labelStyle}>Time window, optional</label>
            <p style={{ fontSize: "16px", color: "#B8B0C8", margin: "0 0 10px" }}>
              Leave blank if this special runs all day, like a $1.50 taco Tuesday.
            </p>
            <div style={{ display: "flex", gap: "12px", marginBottom: "18px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ ...labelStyle, fontSize: "16px" }}>Start</label>
                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ ...labelStyle, fontSize: "16px" }}>End</label>
                <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} style={inputStyle} />
              </div>
            </div>
          </>
        )}

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            type="submit"
            disabled={saving || !selectedBarId}
            style={{
              background: saved ? "#4ADE80" : "linear-gradient(90deg, #6D28D9, #A855F7)",
              border: "none",
              padding: "14px",
              borderRadius: "8px",
              color: "#fff",
              fontWeight: 600,
              fontSize: "15px",
              cursor: "pointer",
              flex: 1,
            }}
          >
            {saving ? "Saving..." : saved ? "✓ Saved!" : editingId ? "Save Changes" : "Add Special"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.2)",
                padding: "14px 20px",
                borderRadius: "8px",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {selectedBarId && (
        <>
          <h3 style={{ marginBottom: "10px" }}>Current Specials</h3>
          {existingSpecials.length === 0 ? (
            <p style={{ color: "#B8B0C8", marginBottom: "20px" }}>No specials added yet.</p>
          ) : (
            existingSpecials.map((item) => renderItemRow(item))
          )}

          <h3 style={{ margin: "24px 0 10px" }}>Birthday Deals</h3>
          {existingBirthdayDeals.length === 0 ? (
            <p style={{ color: "#B8B0C8" }}>No birthday deals added yet.</p>
          ) : (
            existingBirthdayDeals.map((item) => renderItemRow(item))
          )}
        </>
      )}
    </div>
  );
}
